import React, { useState, useEffect } from "react";
import { useSnackbar } from "notistack";
import {
  Button,
  FormControl,
  TableCell,
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TextField,
  Grid,
  Menu,
  MenuItem,
  Divider,
  ListSubheader,
  makeStyles,
} from "@material-ui/core";
import { Autocomplete } from "@material-ui/lab";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  ArrowDropDown as ArrowDropDownIcon,
} from "@material-ui/icons";
import axios from "axios";
import { connect } from "redux-zero/react";
import actions from "../../actions";
import moment from "moment";
import ConfirmDialog from "../ConfirmDialog";
import { formatMoney, accountLabel } from "../../utils";
import TransactionFilters, { blankTransactionFilters } from "./TransactionFilters";
import DateField from "../DateField";

const useStyles = makeStyles((theme) => ({
  modal: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    height: 500,
  },
  paper: {
    backgroundColor: theme.palette.background.paper,
    border: "2px solid #000",
    boxShadow: theme.shadows[5],
    padding: theme.spacing(2, 4, 3),
    width: 800,
  },
  formControl: {
    margin: theme.spacing(1),
    width: "49%",
  },
  formControlFullWidth: {
    margin: theme.spacing(1, 0),
    width: "100%",
  },
  actionButton: {
    cursor: "pointer",
  },
  deleteEntry: {
    cursor: "pointer",
    marginTop: 13,
  },
}));

const mapToProps = ({ selectedSociety }) => ({ selectedSociety });

function BankReceipts({ selectedSociety }) {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();

  const blankFormValue = {
    docNo: 0,
    date: moment().format("YYYY-MM-DD"),
    debits: [],
    credits: [],
    narration: "",
  };

  const [recordAdded, setRecordAdded] = useState(1); // Record Added OR Record Edit
  const [errors, setErrors] = useState([]);
  const [mode, setMode] = useState("Create");
  const [model, setModel] = useState(Object.assign({}, blankFormValue));
  const [accounts, setAccounts] = useState([]);
  const [narrations, setNarrations] = useState([]);

  const [transactions, setTransactions] = useState([]);
  const [transactionsLimit, setTransactionsLimit] = useState(5);
  const [refreshTransactions, setRefreshTransactions] = useState(1);

  const [yearEndMenuAnchor, setYearEndMenuAnchor] = useState(null);
  const [newYearMenuAnchor, setNewYearMenuAnchor] = useState(null);
  const [transactionTemplates, setTransactionTemplates] = useState([]);

  const [deleteModel, setDeleteModel] = React.useState();
  const [showConfirmDelete, setShowConfirmDelete] = React.useState(false);

  const [filters, setFilters] = useState(Object.assign({}, blankTransactionFilters));
  const [appliedFilters, setAppliedFilters] = useState(Object.assign({}, blankTransactionFilters));

  const blankNewEntryValue = { account: { code: "", title: "" }, amount: "" };
  const [newDebitEntry, setNewDebitEntry] = useState(blankNewEntryValue);
  const [newCreditEntry, setNewCreditEntry] = useState(blankNewEntryValue);

  useEffect(() => {
    axios
      .get("/account/selectitems/all", {
        headers: { "X-SocietyId": selectedSociety },
      })
      .then((response) => {
        setAccounts(response.data);
      });
  }, [selectedSociety]);

  useEffect(() => {
    axios
      .get("/transactiontemplate/list", {
        headers: { "X-SocietyId": selectedSociety },
      })
      .then((response) => {
        setTransactionTemplates(response.data);
      });
  }, [selectedSociety]);

  useEffect(() => {
    axios
      .get("/narration/list", { headers: { "X-SocietyId": selectedSociety } })
      .then((response) => {
        setNarrations(response.data);
      });
  }, [selectedSociety, recordAdded]);

  useEffect(() => {
    axios
      .get("/journalvoucher/list/desc/" + transactionsLimit, {
        headers: { "X-SocietyId": selectedSociety },
        params: {
          dateFrom: appliedFilters.dateFrom || undefined,
          dateTo: appliedFilters.dateTo || undefined,
          narration: appliedFilters.narration || undefined,
          amount: appliedFilters.amount || undefined,
        },
      })
      .then((response) => {
        setTransactions(response.data);
      });
  }, [
    selectedSociety,
    recordAdded,
    transactionsLimit,
    refreshTransactions,
    appliedFilters,
  ]);

  function handleFilterChange(field, value) {
    setFilters({ ...filters, [field]: value });
  }

  function applyFilters() {
    setTransactionsLimit(5);
    setAppliedFilters(Object.assign({}, filters));
  }

  function clearFilters() {
    setFilters(Object.assign({}, blankTransactionFilters));
    setTransactionsLimit(5);
    setAppliedFilters(Object.assign({}, blankTransactionFilters));
  }

  function loadMore() {
    setTransactionsLimit(transactionsLimit + 10);
  }

  function edit(row) {
    let newErrors = [];
    setErrors(newErrors);

    axios
      .get("/journalvoucher/" + row.docNo, {
        headers: { "X-SocietyId": selectedSociety },
      })
      .then((response) => {
        setModel(response.data);
        setRecordAdded(recordAdded + 1);
        setMode("Edit");
        setTimeout(() => document.getElementById("debitaccount").focus(), 200);
      });
  }

  function ddelete(row) {
    setDeleteModel(row);
    setShowConfirmDelete(true);
  }

  function runYearEndTransaction(action) {
    setYearEndMenuAnchor(null);
    action();
  }

  function runNewYearTransaction(template) {
    setNewYearMenuAnchor(null);
    addTemplateTransaction(template, true);
  }

  function addDepreciationTransaction() {
    axios({
      method: "POST",
      url: "/journalvoucher/depreciation",
      headers: { "X-SocietyId": selectedSociety },
    })
      .then((response) => {
        setModel(response.data.journalVoucher);
        setRecordAdded(recordAdded + 1);
        setMode("Edit");
        setTimeout(() => document.getElementById("debitaccount").focus(), 200);
      })
      .catch((error) => {
        if (error.response && error.response.status === 409) {
          const yearFrom = moment(error.response.data.periodFrom).format("YYYY");
          const yearTo = moment(error.response.data.periodTo).format("YYYY");
          alert(`Entry already exists for year ${yearFrom}-${yearTo}`);
        } else {
          alert("Error: " + error.response.data.message);
        }
      });
  }

  function addSurplusDeficitTransaction() {
    axios
      .get("/journalvoucher/depreciation", {
        headers: { "X-SocietyId": selectedSociety },
      })
      .then((response) => {
        if (
          !response.data.alreadyCalculated &&
          !window.confirm(
            "Depreciation transaction is not added. Do you want to continue?"
          )
        )
          return;
        createSurplusDeficitTransaction();
      })
      .catch((error) => {
        alert("Error: " + error.response.data.message);
      });
  }

  function createSurplusDeficitTransaction() {
    axios({
      method: "POST",
      url: "/journalvoucher/surplusdeficit",
      headers: { "X-SocietyId": selectedSociety },
    })
      .then((response) => {
        setModel(response.data.journalVoucher);
        setRecordAdded(recordAdded + 1);
        setMode("Edit");
        setTimeout(() => document.getElementById("debitaccount").focus(), 200);
      })
      .catch((error) => {
        if (error.response && error.response.status === 409) {
          const yearFrom = moment(error.response.data.periodFrom).format("YYYY");
          const yearTo = moment(error.response.data.periodTo).format("YYYY");
          alert(`Entry already exists for year ${yearFrom}-${yearTo}`);
        } else {
          alert("Error: " + error.response.data.message);
        }
      });
  }

  function addSurplusToReserveFundTransaction() {
    axios({
      method: "POST",
      url: "/journalvoucher/surplustoreservefund",
      headers: { "X-SocietyId": selectedSociety },
    })
      .then((response) => {
        setModel(response.data.journalVoucher);
        setRecordAdded(recordAdded + 1);
        setMode("Edit");
        setTimeout(() => document.getElementById("debitaccount").focus(), 200);
      })
      .catch((error) => {
        if (error.response && error.response.status === 409) {
          const yearFrom = moment(error.response.data.periodFrom).format("YYYY");
          const yearTo = moment(error.response.data.periodTo).format("YYYY");
          alert(`Entry already exists for year ${yearFrom}-${yearTo}`);
        } else {
          alert(error.response.data.message);
        }
      });
  }

  // reverse passes the New-Year transaction, which reverses the year-end transaction of the previous year
  function addTemplateTransaction(template, reverse = false) {
    axios({
      method: "POST",
      url: "/journalvoucher/template/" + template.id + (reverse ? "/reverse" : ""),
      headers: { "X-SocietyId": selectedSociety },
    })
      .then((response) => {
        setModel(response.data.journalVoucher);
        setRecordAdded(recordAdded + 1);
        setMode("Edit");
        setTimeout(() => document.getElementById("debitaccount").focus(), 200);
      })
      .catch((error) => {
        if (error.response && error.response.status === 409) {
          const yearFrom = moment(error.response.data.periodFrom).format("YYYY");
          const yearTo = moment(error.response.data.periodTo).format("YYYY");
          alert(`${template.name}${reverse ? " New-Year" : ""} entry already exists for year ${yearFrom}-${yearTo}`);
        } else {
          alert("Error: " + error.response.data.message);
        }
      });
  }

  function save() {
    // If you enter new value in freeSolo Autocomplete and don't press enter, the value is not added to the model
    // If you assign the value to the model on keyUp or any other event the filtering does not work correctly
    // Hence, the below workaround
    model.narration = document.getElementById("narration").value;

    let newErrors = [];
    if (!model.date) newErrors.push("Date is required");
    if (model.debits.length === 0)
      newErrors.push("At least one Debit entry is required");
    if (model.credits.length === 0)
      newErrors.push("At least one Credit entry is required");

    const debitTotal = model.debits
      .map((o) => o.amount)
      .reduce((a, b) => a + b, 0);
    const creditTotal = model.credits
      .map((o) => o.amount)
      .reduce((a, b) => a + b, 0);
    if (debitTotal !== creditTotal)
      newErrors.push("Total Debit should match Total Credit");

    setErrors(newErrors);

    if (newErrors.length > 0) return;

    const value = Object.assign({}, model);
    if (!value.narration) value.narration = null;

    if (mode === "Create") {
      axios({
        method: "POST",
        url: "/journalvoucher",
        headers: { "X-SocietyId": selectedSociety },
        data: JSON.stringify(value),
      })
        .then((response) => {
          blankFormValue.date = model.date;

          setModel(Object.assign({}, blankFormValue));
          setRecordAdded(recordAdded + 1);

          enqueueSnackbar("Saved", { variant: "success" });
          setTimeout(
            () => document.getElementById("debitaccount").focus(),
            200
          );
        })
        .catch((error) => {
          let newErrors = ["Error: " + error.response.data.message];
          setErrors(newErrors);
        });
    } else {
      axios({
        method: "PUT",
        url: "/journalvoucher/" + model.docNo,
        headers: { "X-SocietyId": selectedSociety },
        data: JSON.stringify(value),
      })
        .then((response) => {
          blankFormValue.date = model.date;

          setModel(Object.assign({}, blankFormValue));
          setRecordAdded(recordAdded + 1);
          setMode("Create");

          enqueueSnackbar("Saved", { variant: "success" });
          setTimeout(
            () => document.getElementById("debitaccount").focus(),
            200
          );
        })
        .catch((error) => {
          let newErrors = ["Error: " + error.response.data.message];
          setErrors(newErrors);
        });
    }
  }

  function closeConfirmDeleteDialog() {
    setShowConfirmDelete(false);
  }

  function deleteTransaction(row) {
    closeConfirmDeleteDialog();
    axios({
      method: "DELETE",
      url: "/journalvoucher/" + row.docNo,
      headers: { "X-SocietyId": selectedSociety },
    })
      .then((response) => {
        setRefreshTransactions(refreshTransactions + 1);
      })
      .catch((error) => {
        enqueueSnackbar(
          (error.response && error.response.data && error.response.data.message) || "Deleting failed",
          { variant: "error" }
        );
      });
  }

  function addDebitEntry() {
    const entry = {
      account: {
        code: newDebitEntry.account.code,
        title: newDebitEntry.account.title,
      },
      amount: newDebitEntry.amount,
    };

    if (!entry.account.code || !entry.amount) {
      alert("Account and Amount are required!");
      setTimeout(() => document.getElementById("debitaccount").focus(), 200);
      return;
    }

    model.debits.push(entry);
    setModel(Object.assign({}, model));
    setNewDebitEntry(Object.assign({}, blankNewEntryValue));
    setTimeout(() => document.getElementById("debitaccount").focus(), 200);
  }

  function addCreditEntry() {
    const entry = {
      account: {
        code: newCreditEntry.account.code,
        title: newCreditEntry.account.title,
      },
      amount: newCreditEntry.amount,
    };

    if (!entry.account.code || !entry.amount) {
      alert("Account and Amount are required!");
      setTimeout(() => document.getElementById("creditaccount").focus(), 200);
      return;
    }

    model.credits.push(entry);
    setModel(Object.assign({}, model));
    setNewCreditEntry(Object.assign({}, blankNewEntryValue));
    setTimeout(() => document.getElementById("creditaccount").focus(), 200);
  }

  function deleteDebitEntry(entry) {
    var index = model.debits.indexOf(entry);
    if (index !== -1) model.debits.splice(index, 1);
    setModel(Object.assign({}, model));
  }

  function deleteCreditEntry(entry) {
    var index = model.credits.indexOf(entry);
    if (index !== -1) model.credits.splice(index, 1);
    setModel(Object.assign({}, model));
  }

  const reverseTemplates = transactionTemplates.filter(
    (o) => o.type === "nton" && o.json.reverseApplicable
  );

  return (
    <>
      {window.decodedJwt.role === "Administrator" && (
        <div style={{ textAlign: "right" }}>
          <Button
            variant="contained"
            size="small"
            color="primary"
            type="button"
            endIcon={<ArrowDropDownIcon />}
            onClick={(e) => setNewYearMenuAnchor(e.currentTarget)}
            style={{ marginRight: 8 }}
          >
            New-Year Transactions
          </Button>
          <Menu
            anchorEl={newYearMenuAnchor}
            open={Boolean(newYearMenuAnchor)}
            onClose={() => setNewYearMenuAnchor(null)}
            getContentAnchorEl={null}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
          >
            {reverseTemplates.length > 0 && (
              <ListSubheader>Templates</ListSubheader>
            )}
            {reverseTemplates.map((template) => (
              <MenuItem
                key={template.id}
                onClick={() => runNewYearTransaction(template)}
              >
                {template.name} (Reverse)
              </MenuItem>
            ))}
            {reverseTemplates.length === 0 && (
              <MenuItem disabled>No templates with Reverse Applicable</MenuItem>
            )}
          </Menu>
          <Button
            variant="contained"
            size="small"
            color="primary"
            type="button"
            endIcon={<ArrowDropDownIcon />}
            onClick={(e) => setYearEndMenuAnchor(e.currentTarget)}
          >
            Year-end Transactions
          </Button>
          <Menu
            anchorEl={yearEndMenuAnchor}
            open={Boolean(yearEndMenuAnchor)}
            onClose={() => setYearEndMenuAnchor(null)}
            getContentAnchorEl={null}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
          >
            {transactionTemplates.length > 0 && (
              <ListSubheader>Templates</ListSubheader>
            )}
            {transactionTemplates.map((template) => (
              <MenuItem
                key={template.id}
                onClick={() =>
                  runYearEndTransaction(() => addTemplateTransaction(template))
                }
              >
                {template.name}
              </MenuItem>
            ))}
            {transactionTemplates.length > 0 && <Divider />}
            <MenuItem
              onClick={() => runYearEndTransaction(addDepreciationTransaction)}
            >
              Add Depreciation Transaction
            </MenuItem>
            <MenuItem
              onClick={() => runYearEndTransaction(addSurplusDeficitTransaction)}
            >
              Add Surplus/Deficit Transaction
            </MenuItem>
            <MenuItem
              onClick={() =>
                runYearEndTransaction(addSurplusToReserveFundTransaction)
              }
            >
              Add 25% Surplus Transaction
            </MenuItem>
          </Menu>
        </div>
      )}
      <form
        key={recordAdded}
        autoComplete="off"
        onKeyDown={(e) => {
          if (
            e.keyCode === 13 &&
            e.target.id !== "save" &&
            e.target.id !== "addDebitEntry" &&
            e.target.id !== "addCreditEntry"
          )
            e.preventDefault();
        }}
      >
        <div>
          <FormControl className={classes.formControlFullWidth}>
            <DateField
              name="date"
              label="Date"
              fullWidth
              variant="outlined"
              size="small"
              InputLabelProps={{ shrink: true }}
              value={model.date}
              onChange={(date) => {
                setModel({
                  ...model,
                  date,
                });
              }}
              onKeyUp={(e) => {
                if (e.keyCode === 13) {
                  document.getElementById("debitaccount").focus();
                }
              }}
            />
          </FormControl>
        </div>
        <div>
          <Grid container>
            <Grid item xs={6}>
              <h4>
                Debits -{" "}
                {model.debits.map((o) => o.amount).reduce((a, b) => a + b, 0)}
              </h4>
              {model.debits.map((debit, index) => (
                <Grid key={index} container spacing={2}>
                  <Grid item xs={7}>
                    <FormControl className={classes.formControlFullWidth}>
                      {accountLabel(accounts.find((o) => o.code === debit.account.code) || debit.account)}
                    </FormControl>
                  </Grid>
                  <Grid item xs={3}>
                    <FormControl className={classes.formControlFullWidth}>
                      {debit.amount}
                    </FormControl>
                  </Grid>
                  <Grid item xs={1}>
                    <DeleteIcon
                      className={classes.deleteEntry}
                      onClick={() => deleteDebitEntry(debit)}
                    />
                  </Grid>
                </Grid>
              ))}
              <Grid container spacing={2}>
                <Grid item xs={7}>
                  <FormControl className={classes.formControlFullWidth}>
                    <Autocomplete
                      id="debitaccount"
                      name="code"
                      value={newDebitEntry.account}
                      options={accounts}
                      getOptionLabel={accountLabel}
                      selectOnFocus
                      disableOpenOnFocus
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="outlined"
                          size="small"
                          InputLabelProps={{ shrink: true }}
                          label="Account"
                        />
                      )}
                      onChange={(_, value) => {
                        if (value === null) value = blankNewEntryValue.account;
                        setNewDebitEntry({ ...newDebitEntry, account: value });
                      }}
                      title={newDebitEntry.account.groupName}
                      onKeyUp={(e) => {
                        if (e.keyCode === 13) {
                          document.getElementById("debitamount").focus();
                        }
                      }}
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={3}>
                  <FormControl className={classes.formControlFullWidth}>
                    <TextField
                      id="debitamount"
                      type="number"
                      name="amount"
                      value={newDebitEntry.amount}
                      label="Amount"
                      fullWidth
                      variant="outlined"
                      size="small"
                      InputLabelProps={{ shrink: true }}
                      onChange={(e) => {
                        setNewDebitEntry({
                          ...newDebitEntry,
                          amount: parseFloat(e.target.value),
                        });
                      }}
                      onKeyUp={(e) => {
                        if (e.keyCode === 13) {
                          document.getElementById("addDebitEntry").focus();
                        }
                      }}
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={1}>
                  <Button
                    id="addDebitEntry"
                    className={classes.deleteEntry}
                    size="small"
                    onClick={addDebitEntry}
                  >
                    Add
                  </Button>
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={6}>
              <h4>
                Credits -{" "}
                {model.credits.map((o) => o.amount).reduce((a, b) => a + b, 0)}
              </h4>
              {model.credits.map((credit, index) => (
                <Grid key={index} container spacing={2}>
                  <Grid item xs={7}>
                    <FormControl className={classes.formControlFullWidth}>
                      {accountLabel(accounts.find((o) => o.code === credit.account.code) || credit.account)}
                    </FormControl>
                  </Grid>
                  <Grid item xs={3}>
                    <FormControl className={classes.formControlFullWidth}>
                      {credit.amount}
                    </FormControl>
                  </Grid>
                  <Grid item xs={1}>
                    <DeleteIcon
                      className={classes.deleteEntry}
                      onClick={() => deleteCreditEntry(credit)}
                    />
                  </Grid>
                </Grid>
              ))}
              <Grid container spacing={2}>
                <Grid item xs={7}>
                  <FormControl className={classes.formControlFullWidth}>
                    <Autocomplete
                      id="creditaccount"
                      name="code"
                      value={newCreditEntry.account}
                      options={accounts}
                      getOptionLabel={accountLabel}
                      selectOnFocus
                      disableOpenOnFocus
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="outlined"
                          size="small"
                          InputLabelProps={{ shrink: true }}
                          label="Account"
                        />
                      )}
                      onChange={(_, value) => {
                        if (value === null) value = blankNewEntryValue.account;
                        setNewCreditEntry({
                          ...newCreditEntry,
                          account: value,
                        });
                      }}
                      title={newCreditEntry.account.groupName}
                      onKeyUp={(e) => {
                        if (e.keyCode === 13) {
                          document.getElementById("creditamount").focus();
                        }
                      }}
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={3}>
                  <FormControl className={classes.formControlFullWidth}>
                    <TextField
                      id="creditamount"
                      type="number"
                      name="amount"
                      value={newCreditEntry.amount}
                      label="Amount"
                      fullWidth
                      variant="outlined"
                      size="small"
                      InputLabelProps={{ shrink: true }}
                      onChange={(e) => {
                        setNewCreditEntry({
                          ...newCreditEntry,
                          amount: parseFloat(e.target.value),
                        });
                      }}
                      onKeyUp={(e) => {
                        if (e.keyCode === 13) {
                          document.getElementById("addCreditEntry").focus();
                        }
                      }}
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={1}>
                  <Button
                    id="addCreditEntry"
                    className={classes.deleteEntry}
                    size="small"
                    onClick={addCreditEntry}
                  >
                    Add
                  </Button>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </div>
        <div>
          <b>Difference:</b>{" "}
          {model.debits.map((o) => o.amount).reduce((a, b) => a + b, 0) -
            model.credits.map((o) => o.amount).reduce((a, b) => a + b, 0)}
        </div>
        <div>
          <FormControl className={classes.formControlFullWidth}>
            <Autocomplete
              id="narration"
              name="narration"
              options={narrations.map((option) => option.description)}
              freeSolo={true}
              disableOpenOnFocus
              renderInput={(params) => (
                <TextField
                  {...params}
                  InputLabelProps={{ shrink: true }}
                  size="small"
                  label="Narration"
                />
              )}
              value={model.narration}
              onChange={(_, value) => {
                setModel({ ...model, narration: value });
              }}
              onKeyUp={(e) => {
                if (e.keyCode === 13) {
                  document.getElementById("save").focus();
                }
              }}
            />
          </FormControl>
        </div>
        <div>
          {errors.map((error, index) => (
            <div key={index}>{error}</div>
          ))}
        </div>
        <div>
          {window.decodedJwt.role === "Administrator" && (
            <Button
              id="save"
              variant="contained"
              size="small"
              color="primary"
              type="button"
              onClick={save}
            >
              Save
            </Button>
          )}
        </div>
      </form>

      <TransactionFilters
        filters={filters}
        onChange={handleFilterChange}
        onSearch={applyFilters}
        onClear={clearFilters}
        showAccounts={false}
        showChequeNo={false}
        showChequeBank={false}
      />

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Vch No</TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Amount</TableCell>
              <TableCell>Narration</TableCell>
              <TableCell></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {transactions.map((row, index) => (
              <TableRow hover key={index}>
                <TableCell>{row.docNo}</TableCell>
                <TableCell>{moment(row.date).format("DD/MM/YYYY")}</TableCell>
                <TableCell>{formatMoney(row.amount)}</TableCell>
                <TableCell>{row.narration}</TableCell>
                {window.decodedJwt.role === "Administrator" && (
                  <TableCell>
                    <EditIcon
                      className={classes.actionButton}
                      onClick={() => edit(row)}
                    />
                    <DeleteIcon
                      className={classes.actionButton}
                      onClick={() => ddelete(row)}
                    />
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Button onClick={loadMore}>More...</Button>

      <ConfirmDialog
        model={deleteModel}
        show={showConfirmDelete}
        title="Confirm Delete"
        message="Are you sure you want to delete this transaction?"
        onClose={closeConfirmDeleteDialog}
        onYes={deleteTransaction}
      />
    </>
  );
}

export default connect(mapToProps, actions)(BankReceipts);
